import { drawRaster } from '@/engine/canvas';
import { makeCanvas, ctxOf } from '@/engine/canvas';
import type { Seg } from '@/engine/math';
import { raySegment, TAU } from '@/engine/math';

/**
 * 2D visibility polygon by ray casting against occluder segments.
 * Returns flat [x0,y0,x1,y1,...] in world coords, sorted by angle.
 */
export interface VisResult {
  pts: Float32Array;
  n: number;
}

const scratch = { pts: new Float32Array(4096), angles: new Float64Array(2048) };
const angleList: number[] = [];

export interface Bounds { x0: number; y0: number; x1: number; y1: number }

export function visibility(
  ox: number, oy: number, radius: number, segs: Seg[],
  arc?: { center: number; half: number },
  out?: VisResult,
  bounds?: Bounds,
): VisResult {
  // 1. cull segments to the radius box, or to a tighter caller-supplied box (e.g. the screen)
  const near: Seg[] = [];
  const r2 = radius + 2;
  const bx0 = bounds ? Math.max(ox - r2, bounds.x0) : ox - r2;
  const bx1 = bounds ? Math.min(ox + r2, bounds.x1) : ox + r2;
  const by0 = bounds ? Math.max(oy - r2, bounds.y0) : oy - r2;
  const by1 = bounds ? Math.min(oy + r2, bounds.y1) : oy + r2;
  for (const s of segs) {
    const minx = Math.min(s.ax, s.bx), maxx = Math.max(s.ax, s.bx);
    const miny = Math.min(s.ay, s.by), maxy = Math.max(s.ay, s.by);
    if (maxx < bx0 || minx > bx1 || maxy < by0 || miny > by1) continue;
    near.push(s);
  }
  // 2. candidate angles
  angleList.length = 0;
  const inArc = (a: number) => {
    if (!arc) return true;
    let d = (a - arc.center) % TAU;
    if (d > Math.PI) d -= TAU;
    if (d < -Math.PI) d += TAU;
    return Math.abs(d) <= arc.half;
  };
  const addAngle = (a: number) => {
    if (inArc(a)) angleList.push(a);
  };
  for (const s of near) {
    const a1 = Math.atan2(s.ay - oy, s.ax - ox);
    const a2 = Math.atan2(s.by - oy, s.bx - ox);
    addAngle(a1 - 0.0005); addAngle(a1); addAngle(a1 + 0.0005);
    addAngle(a2 - 0.0005); addAngle(a2); addAngle(a2 + 0.0005);
  }
  // sweep at fixed intervals so the circle boundary is round; a wide radius only ever ends up
  // off-screen, so it does not need a finely tessellated rim
  const step = radius > 300 && !arc ? Math.PI / 14 : Math.PI / 36;
  if (arc) {
    const start = arc.center - arc.half, end = arc.center + arc.half;
    angleList.push(start, end);
    for (let a = start + step; a < end; a += step) angleList.push(a);
  } else {
    for (let a = -Math.PI; a < Math.PI; a += step) angleList.push(a);
  }
  // normalise angles relative to arc center so sorting is stable across the -PI/PI seam
  const base = arc ? arc.center - Math.PI : 0;
  const norm = (a: number) => {
    let d = (a - base) % TAU;
    if (d < 0) d += TAU;
    return d;
  };
  angleList.sort((a, b) => norm(a) - norm(b));

  // 3. cast
  const res = out ?? { pts: scratch.pts, n: 0 };
  let n = 0;
  if (arc) {
    res.pts[n * 2] = ox; res.pts[n * 2 + 1] = oy; n++;
  }
  let lastA = NaN;
  for (const a of angleList) {
    if (Math.abs(a - lastA) < 1e-6) continue;
    lastA = a;
    const dx = Math.cos(a), dy = Math.sin(a);
    let best = radius;
    for (const s of near) {
      const t = raySegment(ox, oy, dx, dy, s);
      if (t !== null && t < best) best = t;
    }
    if (n * 2 + 2 > res.pts.length) break;
    res.pts[n * 2] = ox + dx * best;
    res.pts[n * 2 + 1] = oy + dy * best;
    n++;
  }
  res.n = n;
  return res;
}

/** Line of sight test between two points. */
export function hasLos(ax: number, ay: number, bx: number, by: number, segs: Seg[]): boolean {
  const dx = bx - ax, dy = by - ay;
  const minx = Math.min(ax, bx), maxx = Math.max(ax, bx), miny = Math.min(ay, by), maxy = Math.max(ay, by);
  for (const s of segs) {
    if (Math.max(s.ax, s.bx) < minx || Math.min(s.ax, s.bx) > maxx || Math.max(s.ay, s.by) < miny || Math.min(s.ay, s.by) > maxy) continue;
    const t = raySegment(ax, ay, dx, dy, s);
    if (t !== null && t < 1) return false;
  }
  return true;
}

export function tracePolygon(ctx: CanvasRenderingContext2D, v: VisResult, offX: number, offY: number): void {
  ctx.beginPath();
  for (let i = 0; i < v.n; i++) {
    const x = v.pts[i * 2] - offX, y = v.pts[i * 2 + 1] - offY;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

export interface LightSource {
  x: number; y: number; r: number; color: string; intensity: number;
  arc?: { center: number; half: number };
  /** soft inner radius as fraction (0 = point) */
  inner?: number;
}

/** Beam brightness at a normalised distance (0..1 of the radius) and angle off the beam centre. */
function beamFalloff(d: number, angle: number, half: number, inner: number): number {
  const u = Math.max(0, (d - inner) / (1 - inner));
  const radial = (1 - u * u) * (1 - u * u);
  const angular = Math.pow(Math.cos(angle / half * Math.PI / 2), 1.35);
  return radial * angular;
}

/** The radial gradient stops used for point lights, as fractions of full intensity. */
const POINT_STOPS: Array<[number, number]> = [[0, 1], [0.3, 0.85], [0.65, 0.35], [1, 0]];

/**
 * Alpha a light contributes at a world point, ignoring occlusion: the same falloff the beam
 * texture and the radial gradient draw, so CPU sampling matches the lightmap.
 */
export function lightAlpha(light: LightSource, x: number, y: number): number {
  const dx = x - light.x, dy = y - light.y, d = Math.hypot(dx, dy);
  if (d >= light.r) return 0;
  const intensity = Math.max(0, Math.min(1, light.intensity));
  if (light.arc) {
    // The drawn wedge has no coverage at its own apex pixel: the beam starts one pixel out.
    if (d < 1) return 0;
    let angle = Math.atan2(dy, dx) - light.arc.center;
    angle = Math.abs(((angle + Math.PI) % TAU + TAU) % TAU - Math.PI);
    if (angle >= light.arc.half) return 0;
    return intensity * beamFalloff(d / light.r, angle, light.arc.half, light.inner ?? 0.05);
  }
  const r0 = light.r * (light.inner ?? 0.05);
  const t = d <= r0 ? 0 : (d - r0) / (light.r - r0);
  for (let i = 1; i < POINT_STOPS.length; i++) {
    const [t0, a0] = POINT_STOPS[i - 1], [t1, a1] = POINT_STOPS[i];
    if (t <= t1) return intensity * (a0 + (a1 - a0) * (t - t0) / (t1 - t0));
  }
  return 0;
}

// A continuous angular falloff is baked once, then rotated at draw time. Only the
// optical beam is filtered; world pixels and the line-of-sight clip remain crisp.
const beamCache = new Map<string, HTMLCanvasElement>();
function beamTexture(light: LightSource): HTMLCanvasElement {
  const half = light.arc!.half;
  const key = `${light.color}:${half}:${light.inner ?? 0.05}`;
  let c = beamCache.get(key);
  if (c) return c;
  const size = 256, r = size / 2;
  c = makeCanvas(size, size);
  const ctx = ctxOf(c), img = ctx.createImageData(size, size);
  const rgb = hexToRgb(light.color);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const dx = (x + 0.5 - r) / r, dy = (y + 0.5 - r) / r;
    const d = Math.hypot(dx, dy), angle = Math.abs(Math.atan2(dy, dx));
    if (d >= 1 || angle >= half) continue;
    const i = (y * size + x) * 4;
    img.data[i] = rgb[0]; img.data[i + 1] = rgb[1]; img.data[i + 2] = rgb[2];
    img.data[i + 3] = Math.round(255 * beamFalloff(d, angle, half, light.inner ?? 0.05));
  }
  ctx.putImageData(img, 0, 0);
  // Lightning has a small, finite set of variants, but cap the cache defensively.
  if (beamCache.size > 32) beamCache.delete(beamCache.keys().next().value!);
  beamCache.set(key, c);
  return c;
}

export function drawLight(ctx: CanvasRenderingContext2D, light: LightSource, vis: VisResult, offX: number, offY: number): void {
  ctx.save();
  tracePolygon(ctx, vis, offX, offY);
  ctx.clip();
  const lx = light.x - offX, ly = light.y - offY;
  ctx.globalCompositeOperation = 'lighter';
  if (light.arc) {
    ctx.translate(lx, ly);
    ctx.rotate(light.arc.center);
    ctx.globalAlpha = Math.max(0, Math.min(1, light.intensity));
    ctx.imageSmoothingEnabled = true;
    drawRaster(ctx, beamTexture(light), -light.r, -light.r, light.r * 2, light.r * 2);
  } else {
    const g = ctx.createRadialGradient(lx, ly, light.r * (light.inner ?? 0.05), lx, ly, light.r);
    const [r, gg, b] = hexToRgb(light.color);
    for (const [t, a] of POINT_STOPS) g.addColorStop(t, `rgba(${r},${gg},${b},${light.intensity * a})`);
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(lx, ly, light.r, 0, TAU); ctx.fill();
  }
  ctx.restore();
}

export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
